import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Input, Card, Row, Col, Spin, Empty, Typography, message, Select, Space } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import useAxiosPost from '../../hooks/useAxiosPost';
import useMultiNotification from '../../hooks/useNotification';
import VoucherCard from '../Items/Cards/voucher/VoucherCard';

const { Search } = Input;

type SearchByType = 'voucher' | 'mac';

interface SearchResponse {
  found: boolean;
  voucher?: any;
  vouchers?: any[];
  message?: string;
}

const VoucherSearchComponent: React.FC = () => {
  const navigate = useNavigate();
  const { openNotification, contextHolder } = useMultiNotification();
  const [searchBy, setSearchBy] = useState<SearchByType>('voucher');
  const [searchValue, setSearchValue] = useState<string>('');
  const [vouchers, setVouchers] = useState<any[]>([]);
  const [notFound, setNotFound] = useState<boolean>(false);
  const [hasSearched, setHasSearched] = useState<boolean>(false);
  const lastProcessedErrorCount = useRef(0);

  const [
    searchVoucherData,
    searchResponse,
    searchError,
    searchLoading,
    searchMessage,
    searchErrorCount,
  ] = useAxiosPost<ResponseProp>('vouchers/search');

  // Handle search response
  useEffect(() => {
    if (searchResponse !== null) {
      const responseData: any = searchResponse.responseData as SearchResponse;
      if (responseData) {
        const list = responseData.vouchers ?? (responseData.voucher ? [responseData.voucher] : []);
        if (responseData.found && list.length > 0) {
          setVouchers(list);
          setNotFound(false);
          setHasSearched(true);
        } else {
          setVouchers([]);
          setNotFound(true);
          setHasSearched(true);
        }
      }
    }
  }, [searchResponse]);

  // Handle search error
  useEffect(() => {
    if (
      searchError !== null &&
      searchErrorCount !== 0 &&
      searchErrorCount !== lastProcessedErrorCount.current
    ) {
      lastProcessedErrorCount.current = searchErrorCount;
      openNotification('error', 'Search Failed', searchError);
      setVouchers([]);
      setNotFound(false);
      setHasSearched(false);
    }
  }, [searchError, searchErrorCount, openNotification]);

  const handleSearch = (value: string) => {
    const trimmedValue = value.trim();
    if (!trimmedValue) {
      message.warning(searchBy === 'mac' ? 'Please enter a MAC address' : 'Please enter a voucher number');
      return;
    }
    setSearchValue(trimmedValue);
    setVouchers([]);
    setNotFound(false);
    setHasSearched(false);
    if (searchBy === 'mac') {
      searchVoucherData({ macAddress: trimmedValue });
    } else {
      searchVoucherData({ voucherNumber: trimmedValue });
    }
  };

  return (
    <>
      {contextHolder}
      <Card title="Search Voucher" style={{ marginBottom: '24px' }}>
        <Space.Compact size="large" style={{ display: 'flex', maxWidth: '600px' }}>
          <Select
            value={searchBy}
            onChange={setSearchBy}
            options={[
              { value: 'voucher', label: 'By Voucher #' },
              { value: 'mac', label: 'By MAC Address' },
            ]}
            style={{ width: 160 }}
          />
          <Search
            placeholder={searchBy === 'mac' ? 'Enter MAC address (e.g. AA:BB:CC:11:22:33)' : 'Enter voucher number'}
            allowClear
            value={searchValue}
            onChange={(e) => {
              const val = e.target.value;
              setSearchValue(val);
              if (!val.trim()) {
                setVouchers([]);
                setNotFound(false);
                setHasSearched(false);
              }
            }}
            enterButton={<SearchOutlined />}
            onSearch={handleSearch}
            loading={searchLoading}
            style={{ flex: 1 }}
          />
        </Space.Compact>
      </Card>

      {searchLoading && (
        <div style={{ textAlign: 'center', padding: '50px' }}>
          <Spin size="large" />
          <Typography style={{ marginTop: '16px', display: 'block' }}>Searching voucher...</Typography>
        </div>
      )}

      {!searchLoading && hasSearched && (
        <>
          {notFound ? (
            <Card>
              <Empty
                description={
                  <Typography.Text type="danger" style={{ fontSize: '16px' }}>
                    {searchBy === 'mac'
                      ? <>Voucher not found for MAC: <strong>{searchValue}</strong></>
                      : <>Voucher not found against this number: <strong>{searchValue}</strong></>}
                  </Typography.Text>
                }
              />
            </Card>
          ) : vouchers.length > 0 ? (
            <Row gutter={[16, 16]}>
              {vouchers.map((v) => {
                const batchId = v.batch?.intPkBatchID;
                return (
                  <Col key={v.intPkVoucherID} xs={24} sm={24} md={12} lg={8} xl={6}>
                    <div
                      role={batchId ? 'button' : undefined}
                      tabIndex={batchId ? 0 : undefined}
                      onClick={(e) => {
                        if (!batchId) return;
                        if ((e.target as HTMLElement).closest('button')) return;
                        navigate(`/batch-details/${batchId}`);
                      }}
                      onKeyDown={(e) => {
                        if (!batchId) return;
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          navigate(`/batch-details/${batchId}`);
                        }
                      }}
                      style={{
                        cursor: batchId ? 'pointer' : undefined,
                        outline: 'none',
                      }}
                    >
                      <VoucherCard voucher={v} />
                    </div>
                  </Col>
                );
              })}
            </Row>
          ) : null}
        </>
      )}

      {!searchLoading && !hasSearched && (
        <Card>
          <Empty description={searchBy === 'mac' ? 'Enter a MAC address above to search' : 'Enter a voucher number above to search'} />
        </Card>
      )}
    </>
  );
};

export default VoucherSearchComponent;
